/**
 * useBulkActivationPreview Hook
 *
 * Fetches activation previews for multiple records in parallel,
 * categorizes them into ready/supersession/failed, and manages
 * user opt-out state for records with supersessions.
 *
 * This hook powers the UnifiedActivationModal by providing:
 * - Parallel preview fetching with progress tracking
 * - Categorization based on supersession presence
 * - Checkbox state management for opt-out functionality
 */

import { useState, useCallback } from 'react'
import { apiService } from '@/services/apiService'
import type {
  BulkActivationPreview,
  BulkActivationReadyRecord,
  BulkActivationSupersessionRecord,
  BulkActivationFailedRecord,
} from '../types'
import type { SplicingPreviewData } from '@/pages/modules/data/types'

/**
 * Options for useBulkActivationPreview hook
 */
export interface UseBulkActivationPreviewOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Records to fetch previews for */
  records: Record<string, unknown>[]
  /** Field name for record key (default: 'uid') */
  keyField?: string
  /** Field name for record name (default: 'name') */
  nameField?: string
}

/**
 * Progress state during preview fetch
 */
export interface BulkActivationProgress {
  /** Total records to fetch */
  total: number
  /** Completed (success or failure) */
  completed: number
}

/**
 * Return type from useBulkActivationPreview hook
 */
export interface UseBulkActivationPreviewResult {
  /** Fetch all previews for the provided records */
  fetchPreviews: () => Promise<BulkActivationPreview>
  /** Whether fetch is in progress */
  isLoading: boolean
  /** Progress during fetch */
  progress: BulkActivationProgress
  /** Preview data after fetch completes */
  previewData: BulkActivationPreview | null
  /** Toggle selection for a supersession record */
  toggleRecordSelection: (uid: string) => void
  /** Select all supersession records */
  selectAllSupersessions: () => void
  /** Deselect all supersession records */
  deselectAllSupersessions: () => void
  /** Get UIDs to activate (ready + selected supersessions) */
  getActivationUids: () => string[]
  /** Get count of records that will be activated */
  getActivationCount: () => number
  /** Reset all state */
  reset: () => void
}

/**
 * Hook for fetching and managing bulk activation previews
 *
 * @example
 * ```tsx
 * const { fetchPreviews, previewData, isLoading, toggleRecordSelection, getActivationUids } =
 *   useBulkActivationPreview({
 *     endpoint: '/offerings',
 *     records: selectedRecords,
 *   })
 *
 * // Fetch previews when user clicks activate
 * const handleActivateClick = async () => {
 *   const preview = await fetchPreviews()
 *   if (preview.supersessionRecords.length === 0) {
 *     // Show simple confirm
 *   } else {
 *     // Show UnifiedActivationModal
 *   }
 * }
 *
 * // Execute activation with selected records
 * const handleConfirm = () => {
 *   const uids = getActivationUids()
 *   // Call bulk activate API
 * }
 * ```
 */
export function useBulkActivationPreview({
  endpoint,
  records,
  keyField = 'uid',
  nameField = 'name',
}: UseBulkActivationPreviewOptions): UseBulkActivationPreviewResult {
  // Loading state
  const [isLoading, setIsLoading] = useState(false)

  // Progress tracking
  const [progress, setProgress] = useState<BulkActivationProgress>({
    total: 0,
    completed: 0,
  })

  // Preview data
  const [previewData, setPreviewData] = useState<BulkActivationPreview | null>(null)

  /**
   * Fetch a single record's activation preview
   */
  const fetchSinglePreview = useCallback(async (
    record: Record<string, unknown>
  ): Promise<{
    type: 'ready' | 'supersession' | 'failed'
    data: BulkActivationReadyRecord | BulkActivationSupersessionRecord | BulkActivationFailedRecord
  }> => {
    const uid = String(record[keyField] ?? '')
    const name = String(record[nameField] ?? record[keyField] ?? 'Unknown')
    const effectiveFrom = record.effectiveFrom as string | null ?? null
    const effectiveTo = record.effectiveTo as string | null ?? null

    try {
      const response = await apiService.post<SplicingPreviewData>(
        `${endpoint}/${uid}/activate`,
        { preview: true, createContinuations: true }
      )

      if (!response.success) {
        return {
          type: 'failed',
          data: {
            uid,
            name,
            error: response.errors?.[0]?.message ?? 'Preview failed',
          } as BulkActivationFailedRecord,
        }
      }

      const data = response.data
      const meta = response.meta as { splicingPreviewable?: boolean } | undefined

      // Check if this is previewable and has supersessions
      const hasSupersessions = meta?.splicingPreviewable &&
        data &&
        ((data.supersessions?.length ?? 0) > 0 || (data.continuations?.length ?? 0) > 0)

      if (hasSupersessions && data) {
        return {
          type: 'supersession',
          data: {
            uid,
            name,
            effectiveFrom,
            effectiveTo,
            preview: data,
            selected: true, // Selected by default
          } as BulkActivationSupersessionRecord,
        }
      }

      // No supersessions - ready for activation
      return {
        type: 'ready',
        data: {
          uid,
          name,
          effectiveFrom,
          effectiveTo,
        } as BulkActivationReadyRecord,
      }
    } catch (error) {
      return {
        type: 'failed',
        data: {
          uid,
          name,
          error: error instanceof Error ? error.message : 'Preview failed',
        } as BulkActivationFailedRecord,
      }
    }
  }, [endpoint, keyField, nameField])

  /**
   * Fetch previews for all records in parallel
   */
  const fetchPreviews = useCallback(async (): Promise<BulkActivationPreview> => {
    setIsLoading(true)
    setProgress({ total: records.length, completed: 0 })

    const readyRecords: BulkActivationReadyRecord[] = []
    const supersessionRecords: BulkActivationSupersessionRecord[] = []
    const failedRecords: BulkActivationFailedRecord[] = []

    // Fetch all previews in parallel with concurrency limit
    const CONCURRENCY_LIMIT = 5
    let completed = 0

    // Process in batches to avoid overwhelming the server
    for (let i = 0; i < records.length; i += CONCURRENCY_LIMIT) {
      const batch = records.slice(i, i + CONCURRENCY_LIMIT)
      const results = await Promise.all(
        batch.map(record => fetchSinglePreview(record))
      )

      // Categorize results
      for (const result of results) {
        switch (result.type) {
          case 'ready':
            readyRecords.push(result.data as BulkActivationReadyRecord)
            break
          case 'supersession':
            supersessionRecords.push(result.data as BulkActivationSupersessionRecord)
            break
          case 'failed':
            failedRecords.push(result.data as BulkActivationFailedRecord)
            break
        }
        completed++
      }

      setProgress({ total: records.length, completed })
    }

    const preview: BulkActivationPreview = {
      total: records.length,
      readyRecords,
      supersessionRecords,
      failedRecords,
    }

    setPreviewData(preview)
    setIsLoading(false)

    return preview
  }, [records, fetchSinglePreview])

  /**
   * Toggle selection for a supersession record
   */
  const toggleRecordSelection = useCallback((uid: string) => {
    setPreviewData(prev => {
      if (!prev) return prev

      return {
        ...prev,
        supersessionRecords: prev.supersessionRecords.map(record =>
          record.uid === uid
            ? { ...record, selected: !record.selected }
            : record
        ),
      }
    })
  }, [])

  /**
   * Select all supersession records
   */
  const selectAllSupersessions = useCallback(() => {
    setPreviewData(prev => {
      if (!prev) return prev

      return {
        ...prev,
        supersessionRecords: prev.supersessionRecords.map(record => ({
          ...record,
          selected: true,
        })),
      }
    })
  }, [])

  /**
   * Deselect all supersession records
   */
  const deselectAllSupersessions = useCallback(() => {
    setPreviewData(prev => {
      if (!prev) return prev

      return {
        ...prev,
        supersessionRecords: prev.supersessionRecords.map(record => ({
          ...record,
          selected: false,
        })),
      }
    })
  }, [])

  /**
   * Get UIDs of records to activate (ready + selected supersessions)
   */
  const getActivationUids = useCallback((): string[] => {
    if (!previewData) return []

    const readyUids = previewData.readyRecords.map(r => r.uid)
    const selectedSupersessionUids = previewData.supersessionRecords
      .filter(r => r.selected)
      .map(r => r.uid)

    return [...readyUids, ...selectedSupersessionUids]
  }, [previewData])

  /**
   * Get count of records that will be activated
   */
  const getActivationCount = useCallback((): number => {
    if (!previewData) return 0

    const readyCount = previewData.readyRecords.length
    const selectedSupersessionCount = previewData.supersessionRecords.filter(r => r.selected).length

    return readyCount + selectedSupersessionCount
  }, [previewData])

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setIsLoading(false)
    setProgress({ total: 0, completed: 0 })
    setPreviewData(null)
  }, [])

  return {
    fetchPreviews,
    isLoading,
    progress,
    previewData,
    toggleRecordSelection,
    selectAllSupersessions,
    deselectAllSupersessions,
    getActivationUids,
    getActivationCount,
    reset,
  }
}

export default useBulkActivationPreview
